import React from "react";
import MemberAppAdminPanel from "./MemberAppAdminPanel.jsx";
import {
  changedFeaturePayload, changeFeatureEnabled, contextId, scopedMemberRuntime, scopedOrganizations, scopedVenues,
  validateFeatureRows, validateSaveResponse, verifiedClub,
} from "./memberAdministration.js";

export default function MemberAdministrationBridge({ scope, isCurrentScope, memberApi, ...props }) {
  const { api, notify } = props;
  const [state, setState] = React.useState({ phase: "loading", baseline: [], draft: [], organizations: [], venues: [], club: scope.club, warnings: [], serviceReadiness: null, runtime: null });
  const [reload, setReload] = React.useState(0);
  const epoch = React.useRef(0);
  const live = React.useRef(false);
  const saving = React.useRef(false);
  const notifyRef = React.useRef(notify);
  notifyRef.current = notify;

  React.useEffect(() => {
    const generation = ++epoch.current;
    live.current = true;
    saving.current = false;
    const current = () => live.current && generation === epoch.current && isCurrentScope();
    setState({ phase: "loading", baseline: [], draft: [], organizations: [], venues: [], club: scope.club, warnings: [], serviceReadiness: null, runtime: null });
    async function load() {
      for (const name of ["administrationFeatures", "updateAdministrationFeatures", "administrationOrganizations", "administrationVenues"]) {
        if (typeof api?.[name] !== "function") throw new Error("OPS-Verwaltungs-API fehlt: " + name);
      }
      // Calls run in promise callbacks: even synchronous host errors enter the same failure path.
      const hasClubs = typeof api.administrationClubs === "function";
      const results = await Promise.allSettled([
        Promise.resolve().then(() => api.administrationFeatures(scope.clubId)),
        Promise.resolve().then(() => api.administrationOrganizations()),
        Promise.resolve().then(() => api.administrationVenues(scope.clubId)),
        Promise.resolve().then(() => hasClubs ? api.administrationClubs(scope.organizationId) : null),
        Promise.resolve().then(() => memberApi.get("/admin/realtime-push/readiness")),
        Promise.resolve().then(() => typeof api.addonRuntime === "function" ? api.addonRuntime() : null),
      ]);
      if (!current()) return;
      const warnings = [];
      const featuresResult = results[0];
      if (featuresResult.status !== "fulfilled") throw featuresResult.reason;
      const baseline = validateFeatureRows(featuresResult.value, scope);
      let club = scope.club;
      if (hasClubs) {
        if (results[3].status !== "fulfilled") throw new Error("Clubzuordnung konnte nicht bestätigt werden: " + (results[3].reason?.message || "OPS-Anfrage fehlgeschlagen"));
        club = verifiedClub(results[3].value, scope);
      } else warnings.push("Club-Listen-API fehlt; Zuordnung stammt ausschließlich aus dem autorisierten OPS-Kontext.");
      function ancillary(index, check, label) {
        try {
          if (results[index].status !== "fulfilled") throw results[index].reason;
          return check(results[index].value, scope);
        } catch (error) {
          warnings.push(label + ": " + (error?.message || "Antwort nicht verifiziert"));
          return [];
        }
      }
      const organizations = ancillary(1, scopedOrganizations, "Organisationen nicht verfügbar");
      const venues = ancillary(2, scopedVenues, "Venues nicht verfügbar");
      let serviceReadiness = null;
      const services = results[4];
      if (services.status === "fulfilled" && contextId(services.value?.club_id) === scope.clubId && Array.isArray(services.value?.checks)) serviceReadiness = services.value;
      else warnings.push("Realtime-/Push-Betriebsstatus nicht verifiziert" + (services.status === "rejected" && services.reason?.message ? ": " + services.reason.message : "."));
      const runtimeResult = results[5];
      const evidence = scopedMemberRuntime(runtimeResult.status === "fulfilled" ? runtimeResult.value : null, props.tenantContext?.scope_key);
      if (evidence.warning) warnings.push(evidence.warning);
      if (!current()) return;
      setState({ phase: "loaded", baseline, draft: validateFeatureRows(baseline, scope), organizations, venues, club, warnings, serviceReadiness, runtime: evidence.runtime });
    }
    load().catch(error => {
      if (!current()) return;
      const message = error?.message || "Club-Features konnten nicht geladen werden.";
      setState(previous => ({ ...previous, phase: "error", error: message }));
      notifyRef.current?.(message, "error");
    });
    return () => { live.current = false; ++epoch.current; };
  }, [api, memberApi, scope.fingerprint, reload]);

  const changes = changedFeaturePayload(state.baseline, state.draft);
  const editable = state.phase === "loaded";

  function setFeatureEnabled(key, enabled) {
    if (!editable || saving.current || !live.current || !isCurrentScope()) return;
    setState(previous => ({ ...previous, draft: changeFeatureEnabled(previous.draft, key, enabled) }));
  }

  function setFeatures(update) {
    if (!editable || saving.current || !live.current || !isCurrentScope()) return;
    setState(previous => {
      const copy = validateFeatureRows(previous.draft, scope);
      const proposed = typeof update === "function" ? update(copy) : update;
      const validated = validateFeatureRows(proposed, scope);
      // Legacy panel contract supports activation edits only. Configuration and
      // all unrelated registrations remain anchored to the server baseline.
      const edits = new Map(validated.map(row => [row.feature_key, row.enabled]));
      const draft = previous.draft.reduce((rows, row) => edits.has(row.feature_key)
        ? changeFeatureEnabled(rows, row.feature_key, edits.get(row.feature_key)) : rows, previous.draft);
      return { ...previous, draft };
    });
  }

  async function saveFeatures() {
    if (!editable || saving.current || !live.current || !isCurrentScope()) return;
    const sent = changedFeaturePayload(state.baseline, state.draft);
    if (!sent.length) return;
    const generation = epoch.current;
    const current = () => live.current && epoch.current === generation && isCurrentScope();
    saving.current = true;
    setState(previous => ({ ...previous, phase: "saving", saveError: null }));
    try {
      const response = await api.updateAdministrationFeatures(scope.clubId, sent);
      if (!current()) return;
      const baseline = validateSaveResponse(response, scope, state.baseline, sent);
      setState(previous => ({ ...previous, phase: "loaded", baseline, draft: validateFeatureRows(baseline, scope), saveError: null }));
      notifyRef.current?.("MEMBER-Module für diesen Club gespeichert.");
    } catch (error) {
      if (current()) {
        const message = error?.message || "Moduländerungen konnten nicht bestätigt werden.";
        // A failed response may follow a committed server write. Require an explicit reload.
        setState(previous => ({ ...previous, phase: "save_uncertain", saveError: message }));
        notifyRef.current?.(message, "error");
      }
    } finally {
      if (current()) saving.current = false;
    }
  }

  if (["loading", "error"].includes(state.phase)) return <section className="grid gap-3 p-5" data-testid="member-administration-status">
    <p role={state.phase === "error" ? "alert" : "status"} data-testid={state.phase === "error" ? "member-administration-error" : undefined}>
      {state.phase === "error" ? state.error : "MEMBER-Verwaltungsdaten werden geladen."}
    </p>
    {state.phase === "error" && <button type="button" className="w-fit border px-3 py-2 font-bold" onClick={() => setReload(value => value + 1)}>Erneut laden</button>}
  </section>;

  return <div data-testid="member-administration-status" data-phase={state.phase}>
    {state.warnings.map(message => <p role="status" className="border border-amber-300 p-3 text-sm" key={message}>{message}</p>)}
    {state.saveError && <div role="alert" className="border border-red-400 p-3 text-sm" data-testid="member-administration-error">
      <p>{state.saveError}</p><p>Speicherstand nicht bestätigt. Vor weiteren Änderungen bitte neu laden.</p>
    </div>}
    <div className="flex flex-wrap items-center gap-3 px-4 pt-4 text-sm">
      <span data-testid="member-feature-diff">{changes.length} ungespeicherte Moduländerungen</span>
      <button type="button" className="border px-3 py-2" disabled={state.phase === "saving"} onClick={() => setReload(value => value + 1)}>Verwaltungsdaten neu laden</button>
    </div>
    <MemberAppAdminPanel {...props} memberApi={memberApi} selectedClub={state.club}
      features={state.draft} featureEvidence={state.baseline} featuresLoaded={true}
      featuresEditable={editable} setFeatures={setFeatures} setFeatureEnabled={setFeatureEnabled} saveFeatures={saveFeatures}
      hasFeatureChanges={changes.length > 0} savingFeatures={state.phase === "saving"}
      organizations={state.organizations} venues={state.venues} addonRuntime={state.runtime}
      serviceReadiness={state.serviceReadiness} />
  </div>;
}
